import React, { useState } from 'react';
import { validateSchoolEmail, getTrialConfig } from '../services/schoolEmailValidation';

export default function CreditCardTrialGate({
  email,
  onSuccess,
  onSkip
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const trialConfig = getTrialConfig(email);
  const emailValidation = validateSchoolEmail(email);

  // If school email, no credit card needed
  if (!trialConfig.requiresCreditCard) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              Educational Email Detected
            </h3>
            <div className="mt-2 text-sm text-green-700">
              <p>Your {emailValidation.domain} email has been verified as an educational institution. You can start your full 14-day trial immediately!</p>
            </div>
            <div className="mt-4">
              <button
                onClick={onSuccess}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleCreditCardVerification = async () => {
    setLoading(true);
    setError('');

    try {
      // Import Stripe functions
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase');

      // Create a $0 authorization (setup intent)
      const createSetupIntent = httpsCallable(functions, 'createTrialSetupIntent');

      const result = await createSetupIntent({
        email: email,
        purpose: 'trial_verification'
      });

      if (result.data.success) {
        // Redirect to Stripe for card verification
        window.location.href = result.data.url;
      } else {
        throw new Error(result.data.error || 'Failed to create card verification');
      }

    } catch (err) {
      console.error('Credit card verification error:', err);
      setError('Failed to set up card verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-800">
            Credit Card Verification Required
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              Since you're using a personal email address ({emailValidation.domain}), we need to verify a credit card to prevent trial abuse.
              <strong> No charges will be made during your trial.</strong>
            </p>
            <div className="mt-3">
              <p><strong>What happens:</strong></p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>We'll verify your credit card (no charge)</li>
                <li>You get a {trialConfig.trialDays}-day free trial</li>
                <li>Cancel anytime before trial ends</li>
                <li>Only charged if you continue after trial</li>
              </ul>
            </div>
          </div>

          <div className="mt-4 flex space-x-3">
            <button
              onClick={handleCreditCardVerification}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-bold py-2 px-4 rounded flex items-center"
            >
              {loading && (
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {loading ? 'Setting up...' : '💳 Verify Card & Start Trial'}
            </button>

            {onSkip && (
              <button
                onClick={onSkip}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
              >
                Skip for Now
              </button>
            )}
          </div>

          {error && (
            <div className="mt-3 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="mt-4 text-xs text-yellow-600">
            <p><strong>Have a school email?</strong> Register with your .edu or school district email to skip this step!</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component for email validation display
export function EmailValidationBadge({ email }) {
  const validation = validateSchoolEmail(email);

  if (!validation.isSchoolEmail) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Personal Email
      </span>
    );
  }

  const confidenceColors = {
    high: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-orange-100 text-orange-800'
  };

  const confidenceIcons = {
    high: '✅',
    medium: '🔍',
    low: '❓'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${confidenceColors[validation.confidence]}`}>
      {confidenceIcons[validation.confidence]} School Email ({validation.confidence} confidence)
    </span>
  );
}